import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  loadHealingAsync,
  resolveHealingEventAsync,
  selectHealingEvents,
  selectHealingHideLowSignal,
  selectHealingResolvingId,
  selectHealingSort,
  setHealingHideLowSignal,
  setHealingSort,
} from '../../../redux/locatorDrift/locatorDriftSlice';
import EmptyState from '../shared/EmptyState';
import { healingEventsTableStyles } from './healingStyles';

export default function HealingEventsTable() {
  const dispatch = useDispatch();
  const events = useSelector(selectHealingEvents);
  const sort = useSelector(selectHealingSort);
  const hideLowSignal = useSelector(selectHealingHideLowSignal);
  const resolvingId = useSelector(selectHealingResolvingId);

  return (
    <Paper variant="outlined" sx={healingEventsTableStyles.root}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={healingEventsTableStyles.header}
      >
        <Typography variant="subtitle2" fontWeight={800}>
          Recent Healing Events
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <label style={healingEventsTableStyles.sortLabel}>
            <Typography variant="caption" color="text.secondary">
              Sort
            </Typography>
            <select
              value={sort}
              onChange={(event) => {
                dispatch(setHealingSort(event.target.value));
                dispatch(loadHealingAsync({ sort: event.target.value }));
              }}
              style={healingEventsTableStyles.smallInput}
            >
              <option value="score">Highest score</option>
              <option value="recent">Most recent</option>
            </select>
          </label>
          <FormControlLabel
            control={
              <Switch
                checked={hideLowSignal}
                onChange={(event) => {
                  dispatch(setHealingHideLowSignal(event.target.checked));
                  dispatch(
                    loadHealingAsync({
                      hideLowSignal: event.target.checked,
                    }),
                  );
                }}
              />
            }
            label="Hide low-signal events"
          />
        </Stack>
      </Stack>
      {events.length === 0 ? (
        <EmptyState>No healing events recorded yet.</EmptyState>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Scenario</TableCell>
              <TableCell>Failed Locator</TableCell>
              <TableCell>Top Suggestion</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                <TableCell sx={healingEventsTableStyles.locatorCellScenario}>
                  {event.scenario}
                </TableCell>
                <TableCell sx={healingEventsTableStyles.locatorCell}>
                  {event.locator_value}
                </TableCell>
                <TableCell sx={healingEventsTableStyles.locatorCell}>
                  {event.suggested_value || '-'}
                </TableCell>
                <TableCell>
                  {event.suggestion_score ? event.suggestion_score.toFixed(3) : '-'}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={event.outcome} />
                </TableCell>
                <TableCell align="right">
                  {event.outcome === 'auto_healed' || event.outcome === 'auto_unconfirmed' ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        disabled={resolvingId === event.id}
                        onClick={() =>
                          dispatch(
                            resolveHealingEventAsync({
                              eventId: event.id,
                              outcome: 'accepted',
                            }),
                          )
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={resolvingId === event.id}
                        onClick={() =>
                          dispatch(
                            resolveHealingEventAsync({
                              eventId: event.id,
                              outcome: 'rejected',
                            }),
                          )
                        }
                      >
                        Reject
                      </Button>
                    </Stack>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
